'use client';

import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';

const DataTable = ({
	columns,
	data,
	loading = false,
	pagination = null,
	onPageChange = () => {},
	emptyMessage = 'No data available',
}) => {
	if (loading) {
		return (
			<div className='card'>
				<div className='animate-pulse'>
					<div className='h-4 bg-gray-200 rounded w-1/4 mb-4'></div>
					<div className='space-y-3'>
						{[...Array(5)].map((_, i) => (
							<div
								key={i}
								className='h-4 bg-gray-200 rounded'></div>
						))}
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className='card p-0'>
			<div className='overflow-x-auto'>
				<table className='min-w-full divide-y divide-gray-200'>
					<thead className='bg-gray-50'>
						<tr>
							{columns.map((column, index) => (
								<th
									key={index}
									className='px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider'>
									{column.header}
								</th>
							))}
						</tr>
					</thead>
					<tbody className='bg-white divide-y divide-gray-200'>
						{data.length === 0 ? (
							<tr>
								<td
									colSpan={columns.length}
									className='px-6 py-12 text-center text-gray-500'>
									<div className='flex flex-col items-center'>
										<svg
											className='h-12 w-12 text-gray-300 mb-4'
											fill='none'
											stroke='currentColor'
											viewBox='0 0 24 24'>
											<path
												strokeLinecap='round'
												strokeLinejoin='round'
												strokeWidth={1}
												d='M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'
											/>
										</svg>
										<p>{emptyMessage}</p>
									</div>
								</td>
							</tr>
						) : (
							data.map((row, rowIndex) => (
								<tr
									key={rowIndex}
									className='table-row'>
									{columns.map((column, colIndex) => (
										<td
											key={colIndex}
											className='px-6 py-4 whitespace-nowrap text-sm text-gray-900'>
											{column.render
												? column.render(row)
												: row[column.accessor]}
										</td>
									))}
								</tr>
							))
						)}
					</tbody>
				</table>
			</div>

			{/* Pagination */}
			{pagination && (
				<div className='bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6'>
					<div className='flex-1 flex justify-between sm:hidden'>
						<button
							onClick={() =>
								onPageChange(pagination.currentPage - 1)
							}
							disabled={pagination.currentPage <= 1}
							className='relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'>
							Previous
						</button>
						<button
							onClick={() =>
								onPageChange(pagination.currentPage + 1)
							}
							disabled={
								pagination.currentPage >= pagination.totalPages
							}
							className='ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'>
							Next
						</button>
					</div>
					<div className='hidden sm:flex-1 sm:flex sm:items-center sm:justify-between'>
						<div>
							<p className='text-sm text-gray-700'>
								Showing{' '}
								<span className='font-medium'>
									{(pagination.currentPage - 1) *
										pagination.pageSize +
										1}
								</span>{' '}
								to{' '}
								<span className='font-medium'>
									{Math.min(
										pagination.currentPage *
											pagination.pageSize,
										pagination.total,
									)}
								</span>{' '}
								of{' '}
								<span className='font-medium'>
									{pagination.total}
								</span>{' '}
								results
							</p>
						</div>
						<div>
							<nav className='relative z-0 inline-flex rounded-md shadow-sm -space-x-px'>
								<button
									onClick={() =>
										onPageChange(pagination.currentPage - 1)
									}
									disabled={pagination.currentPage <= 1}
									className='relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'>
									<ChevronLeftIcon className='h-5 w-5' />
								</button>

								{/* Page numbers */}
								{[
									...Array(
										Math.min(pagination.totalPages, 5),
									),
								].map((_, index) => {
									const pageNumber = index + 1;
									const isCurrentPage =
										pageNumber === pagination.currentPage;

									return (
										<button
											key={pageNumber}
											onClick={() =>
												onPageChange(pageNumber)
											}
											className={`
                        relative inline-flex items-center px-4 py-2 border text-sm font-medium
                        ${
							isCurrentPage
								? 'z-10 bg-primary-50 border-primary-500 text-primary-600'
								: 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
						}
                      `}>
											{pageNumber}
										</button>
									);
								})}

								<button
									onClick={() =>
										onPageChange(pagination.currentPage + 1)
									}
									disabled={
										pagination.currentPage >=
										pagination.totalPages
									}
									className='relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed'>
									<ChevronRightIcon className='h-5 w-5' />
								</button>
							</nav>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default DataTable;
